/**
 * View Component: ContentGrid
 * Main content area — posts grouped by date, in card grid.
 *
 * T-Cron-2026-10-28 20:00 晚间 — 改进清单 #13「性能:虚拟滚动 + 触底加载更多」
 *
 * 线上实测(2026-10-28):首屏一次挂载 **473 张卡 / 22,229 个 DOM 节点 /
 * 1.28 MB HTML**,FCP 1704ms。content-visibility:auto(batch.css)只省了
 * 「视口外不绘制」,但 React 仍然要为 473 张卡建组件、建 DOM、绑事件。
 * ⇒ 本组件改为**分组级虚拟滚动**:
 *   · 只挂载窗口内的日期分组(初始 8 组),其余按需挂载;
 *   · 滚到底 → 哨兵触发 → 追加下一批(触底加载更多);
 *   · 窗口超过上限 → 卸载最上面的分组并**补偿滚动位置**(视觉零跳动);
 *   · 想看更早的内容 → 点「加载更早的内容」→ 对称恢复 + 补偿。
 *
 * Props:
 *   groups: [{ date, posts: [] }]        全量分组(由 store 给出,不受窗口影响)
 *   onToggleFav: (id) => void
 *   onCardClick: (id) => void
 *   totalVisible: number
 *   selectionMode: boolean      改进清单 #8:是否处于多选态
 *   selectedIds: Set<string>    改进清单 #8:已选中的 id 集合
 *   onToggleSelect: (id, next) => void
 *   scrollRootRef: React.Ref    .app-main 滚动容器(用于触底判定 + 滚动补偿)
 */
import useProgressiveFeed from '../harness/useProgressiveFeed.js';

function EmptyState({ message }) {
  return (
    <div className="empty-state" role="status" aria-live="polite">
      <svg className="empty-state-icon" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="8" y="8" width="48" height="48" rx="6"></rect>
        <path d="M8 28h48"></path>
        <circle cx="20" cy="18" r="3"></circle>
        <circle cx="32" cy="18" r="3"></circle>
        <path d="M16 40l6-6l4 4l8-8l10 10"></path>
      </svg>
      <div className="empty-state-title">没有找到相关内容</div>
      <div className="empty-state-text">{message || '试试调整筛选条件，或清除筛选查看全部内容。'}</div>
    </div>
  );
}

function ContentGrid({
  groups,
  onToggleFav,
  onCardClick,
  totalVisible,
  allPosts,
  viewMode = 'grid',
  preferredVersion,
  selectionMode = false,
  selectedIds = null,
  onToggleSelect,
  scrollRootRef = null,
}) {
  // 🔴 改进清单 #13:窗口化渲染。groups 始终是全量(store.filtered 的分组),
  //    挂载的只是 windowGroups —— 导出/统计/全选的口径完全不受影响。
  const feed = useProgressiveFeed(groups, { scrollRootRef });

  if (totalVisible === 0) {
    return <EmptyState />;
  }

  return (
    <div
      className="content-grid"
      role="region"
      aria-label="内容列表"
      ref={feed.containerRef}
    >
      {/* 读屏播报:虚拟滚动下 AT 需要知道「列表没全挂载」 */}
      <p className="sr-only" role="status" aria-live="polite">{feed.statusText}</p>

      {/* 「加载更早的内容」—— 有更早的分组被卸载时出现(对称于触底加载) */}
      {feed.hasMoreAbove && (
        <div className="feed-load-earlier">
          <button
            type="button"
            className="feed-load-more-btn"
            onClick={feed.loadEarlier}
          >
            加载更早的内容
          </button>
        </div>
      )}

      {feed.windowGroups.map(group => (
        <section
          key={group.date}
          className="date-group"
          data-screen-label={group.date}
          // 虚拟滚动需要知道每个分组的真实高度(卸载时量、恢复时补偿)
          data-feed-group={group.date}
          // T-Cron-2026-10-25 20:00 — 改进清单 #10「无障碍:aria-label 覆盖率」。
          //
          // 🔴 本轮实测发现的真实缺口(不是理论问题):
          //   `grep -c 'aria-label' ContentGrid.jsx` = **0** —— 整个 src 树里
          //   唯一一个 aria-label 覆盖率是 0 的交互组件。
          //   更严重的是:**全站只有 1 个 <h2>(在 PostDetail 抽屉里)**,
          //   列表区**一个标题元素都没有** ⇒ 读屏用户拿不到任何「按标题跳转」
          //   的导航能力 —— 467 张卡是一个**没有结构**的扁平流。
          //
          // 修法(语义层级,不是贴标签):
          //   · .content-grid   → role="region" + aria-label="内容列表"
          //     (屏幕阅读器的 landmark 列表里能直接跳到这一块)
          //   · 每个日期分组     → <section aria-labelledby="date-h-<date>">
          //     (section 需要可访问名,否则读屏器会念成「region」无名区块)
          //   · 日期书签         → <h2 id="date-h-<date>">
          //     (把日期变成真正的标题,「按标题跳转」才可用)
          //   · 标题里补         → aria-level 冗余保险 + 视觉不变
          //
          // ⚠️ 视觉零回归:.date-bookmark 的样式挂在这个元素上,
          //    把它从 <div> 换成 <h2> 不改 class ⇒ app.css / polish.css
          //    里的 `.date-bookmark{...}` 全部照旧生效。
          //    但 <h2> 有浏览器默认的 margin/font-weight ⇒ 必须显式
          //    reset,否则日期标签的间距会变(这正是 T104/T105 记的
          //    「静默视觉回归」类型)。
          aria-labelledby={`date-h-${group.date}`}
        >
          <div className="date-group-header">
            <h2
              id={`date-h-${group.date}`}
              className="date-bookmark"
            >
              {ContentService.formatDate(group.date)}
              {ContentService.isToday(group.date, allPosts) && (
                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, opacity: 0.8 }}>今日</span>
              )}
            </h2>
            <div className="date-meta">
              <span>{group.posts.length} 篇内容</span>
            </div>
          </div>

          <div className={`post-grid ${viewMode === 'list' ? 'list-mode' : ''}`}>
            {group.posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                onToggleFav={onToggleFav}
                onClick={onCardClick}
                preferredVersion={preferredVersion}
                selectionMode={selectionMode}
                selected={selectedIds ? selectedIds.has(post.id) : false}
                onToggleSelect={onToggleSelect}
              />
            ))}
          </div>
        </section>
      ))}

      {/* 触底加载更多的哨兵:滚到这里就自动追加下一批分组 */}
      {feed.hasMoreBelow && (
        <div className="feed-load-more" ref={feed.bottomSentinelRef}>
          <button
            type="button"
            className="feed-load-more-btn"
            onClick={feed.loadMore}
          >
            触底加载更多
          </button>
          <span className="feed-load-more-hint">
            已加载 {feed.mountedPosts} / {feed.totalPosts} 篇(向下滚动自动加载)
          </span>
        </div>
      )}
    </div>
  );
}

window.ContentGrid = ContentGrid;

export default ContentGrid;
